import { useCallback, useEffect, useRef, useState } from "react";
import { Link, NavLink, useNavigate, useParams } from "react-router-dom";
import { BindingAliasSchema, type FieldType, type InventoryResponse, type ProfileDetailResponse, type ProfileField } from "@starsach/shared-types";
import { useBind, useBindings, useCustomer, useInventory, useProfile, useProfiles, usePublishProfile } from "../../api/hooks";
import { EnvironmentGate, useEnvironmentName, useRememberEnvironment } from "../../components/Environment";
import { Card, ErrorState, LoadingState, MutationError, PageHeader, RelativeTime, StatusPill, btnSecondary, inputClass } from "../../components/ui";
import { hoursNow } from "../../lib/hours";
import { moduleById } from "../../lib/modules";
import { FIELD_TYPE_LABELS } from "../../lib/profile-fields";
import { rememberProfile } from "../../lib/recent-profiles";
import { TOOLTIPS } from "../../lib/tooltips";
import { HoursEditor, MenuEditor } from "../customer/ProfileFieldEditors";
import { PromotionPanel } from "../customer/PromotionPanel";
import { Hint } from "../../components/Hint";
import { ContextBar, InventoryStateNote } from "./ContextBar";
import { EmergencyClose, closureState, describeOverride } from "./EmergencyClose";
import { exportProfile } from "./ImportExport";
import { AddField, Conditions, NewOverride, OverrideRow, Versions } from "./parts";
import { AnnouncementsTab } from "./tabs/AnnouncementsTab";
import { HolidaysTab } from "./tabs/HolidaysTab";
import { HoursTab } from "./tabs/HoursTab";
import { KvTab } from "./tabs/KvTab";

const REFERENCE_TYPES: FieldType[] = ["queue", "flow", "prompt", "hours_of_operation", "lex_bot"];

/**
 * The sections of one profile - tabs in the body, never nav entries
 * (requirements 8.9). Voice, References and Menus are the owner's "not
 * needed" (2026-09-18, Menus after the first walk): a flow sets its voice,
 * reads the Connect cache by name and branches in its own blocks. They
 * stay visible only on a profile that already has such fields, with
 * nothing to add, so nothing authored is orphaned.
 */
const TABS: Array<{ id: string; label: string; types?: FieldType[]; legacy?: boolean }> = [
  { id: "overview", label: "Overview" },
  { id: "hours", label: "Hours", types: ["hours"] },
  { id: "holidays", label: "Holidays" },
  { id: "announcements", label: "Announcements", types: ["announcement"] },
  { id: "kv", label: "Key-value pairs", types: ["kv"] },
  { id: "menus", label: "Menus", types: ["menu"], legacy: true },
  { id: "voice", label: "Voice", types: ["voice"], legacy: true },
  { id: "references", label: "References", types: REFERENCE_TYPES, legacy: true },
  { id: "versions", label: "History" },
  { id: "promote", label: "Promote" },
];

export function ProfilePage() {
  const { environmentId, profileId = "", tab = "overview" } = useParams();
  useRememberEnvironment(environmentId);
  useEffect(() => {
    if (environmentId && profileId) rememberProfile(environmentId, profileId);
  }, [environmentId, profileId]);
  if (!environmentId) return <EnvironmentGate module={moduleById("flow-configuration")} section="profiles" />;
  return <Profile environmentId={environmentId} profileId={profileId} tab={tab} />;
}

function Profile({ environmentId, profileId, tab }: { environmentId: string; profileId: string; tab: string }) {
  const env = useCustomer(environmentId);
  const profiles = useProfiles(environmentId);
  const detail = useProfile(environmentId, profileId);
  const inventory = useInventory(environmentId);
  const bindings = useBindings(environmentId);
  const navigate = useNavigate();
  const base = `/flow-configuration/${environmentId}/profiles`;
  const envName = env.data?.customer.friendly_name ?? environmentId;
  const live = env.data?.customer.live ?? false;
  const profile = profiles.data?.profiles.find((p) => p.profileId === profileId);
  const bound = (bindings.data?.bindings ?? []).filter((b) => b.profileId === profileId).map((b) => b.alias);

  return (
    <>
      <PageHeader title={`Flow configuration · ${profile?.name ?? detail.data?.profile.name ?? "Profile"}`} />
      <div className="grid gap-5">
        {detail.isPending && <LoadingState label="Loading configuration" />}
        {detail.isError && <ErrorState error={detail.error} onRetry={() => void detail.refetch()} />}
        {detail.data && (
          // #264: keyed on the profile only - a save must not remount what is being edited.
          <Editor
            key={profileId}
            environmentId={environmentId}
            envName={envName}
            live={live}
            writesEnabled={(env.data?.enabledComponents ?? []).includes("HoursOfOperationWrites")}
            profileId={profileId}
            tab={tab}
            detail={detail.data}
            inventory={inventory.data}
            bound={bound}
            profiles={profiles.data?.profiles ?? []}
            onProfile={(id) => navigate(`${base}/${id}/${tab}`)}
            onEnvironment={(id) => `/flow-configuration/${id}/profiles/${profileId}/${tab}`}
          />
        )}
      </div>
    </>
  );
}

function Editor({
  environmentId,
  envName,
  live,
  writesEnabled,
  profileId,
  tab,
  detail,
  inventory,
  bound,
  profiles,
  onProfile,
  onEnvironment,
}: {
  environmentId: string;
  envName: string;
  live: boolean;
  /** ADR-0019: the stack reported EnableHoursOfOperationWrites, so a linked schedule is written into Connect. */
  writesEnabled: boolean;
  profileId: string;
  tab: string;
  detail: ProfileDetailResponse;
  inventory: InventoryResponse | undefined;
  bound: string[];
  profiles: Array<{ profileId: string; name: string }>;
  onProfile: (profileId: string) => void;
  onEnvironment: (environmentId: string) => string;
}) {
  const save = usePublishProfile(environmentId, profileId);
  // #264: what is on screen is what is saved, give or take a moment. A gated non-manager edits
  // the open change request instead of the live configuration, and every save extends it.
  const editingRequest = detail.saveAuthority === "needs_approval";
  const initial = editingRequest && detail.pendingRequest ? detail.pendingRequest.fields : detail.fields;
  const [draft, setDraftState] = useState<ProfileField[]>(initial);
  const latest = useRef(draft);
  const saved = useRef(JSON.stringify(initial));
  const savedVersion = useRef(detail.configVersion);
  const inFlight = useRef(false);
  const timer = useRef<number>(undefined);
  const [dirty, setDirty] = useState(false);
  const [lastSave, setLastSave] = useState<{ at: string; promotionId?: string }>();
  const [closing, setClosing] = useState(false);
  const [more, setMore] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);
  const hoursFields = draft.filter((f): f is Extract<ProfileField, { type: "hours" }> => f.type === "hours");
  const announcementNames = draft.filter((f) => f.type === "announcement").map((f) => f.name).sort();
  const active = TABS.find((t) => t.id === tab) ?? TABS[0]!;
  const base = `/flow-configuration/${environmentId}/profiles/${profileId}`;

  const flush = useCallback(() => {
    window.clearTimeout(timer.current);
    const snapshot = JSON.stringify(latest.current);
    if (snapshot === saved.current || inFlight.current) return;
    inFlight.current = true;
    save.mutate(
      { fields: latest.current },
      {
        onSuccess: (r) => {
          saved.current = snapshot;
          if (r.status === "published") savedVersion.current = r.configVersion;
          setLastSave({ at: new Date().toISOString(), ...(r.status === "pending_approval" ? { promotionId: r.promotionId } : {}) });
          setDirty(JSON.stringify(latest.current) !== snapshot);
          inFlight.current = false;
          // Edits made while the save was in flight go in the next one.
          if (JSON.stringify(latest.current) !== snapshot) timer.current = window.setTimeout(flush, 0);
        },
        // A failure waits for Retry or the next edit - never a retry loop against a service that is down.
        onError: () => {
          inFlight.current = false;
        },
      },
    );
  }, [save]);
  // A row action saves at once; a keystroke or a drag saves after a short pause.
  const setDraft = useCallback(
    (next: ProfileField[] | ((prev: ProfileField[]) => ProfileField[]), delay = 0) => {
      latest.current = typeof next === "function" ? next(latest.current) : next;
      setDraftState(latest.current);
      setDirty(JSON.stringify(latest.current) !== saved.current);
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(flush, delay);
    },
    [flush],
  );
  // Leaving the page must not lose a pending edit; closing the tab at least asks.
  useEffect(() => {
    const warn = (e: BeforeUnloadEvent) => {
      if (JSON.stringify(latest.current) !== saved.current) e.preventDefault();
    };
    window.addEventListener("beforeunload", warn);
    return () => {
      window.removeEventListener("beforeunload", warn);
      window.clearTimeout(timer.current);
      if (JSON.stringify(latest.current) !== saved.current && !inFlight.current) save.mutate({ fields: latest.current });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // A version this editor did not produce (a rollback, a promotion, someone else) replaces the draft once it is clean.
  useEffect(() => {
    if (editingRequest || detail.configVersion === savedVersion.current) return;
    if (JSON.stringify(latest.current) !== saved.current || inFlight.current) return;
    latest.current = detail.fields;
    saved.current = JSON.stringify(detail.fields);
    savedVersion.current = detail.configVersion;
    setDraftState(detail.fields);
  }, [detail.configVersion, detail.fields, editingRequest]);

  useEffect(() => {
    if (!more) return;
    const close = (e: MouseEvent) => {
      if (!moreRef.current?.contains(e.target as Node)) setMore(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [more]);

  // By address and against the latest draft, not by identity against this render's: a drag fires
  // faster than React commits, and a stale closure must still land on the right field.
  const address = (f: ProfileField) => `${f.type}/${f.name}`;
  const update = (field: ProfileField, nextField: ProfileField) => setDraft((prev) => prev.map((f) => (address(f) === address(field) ? nextField : f)), 700);
  const remove = (field: ProfileField) => setDraft((prev) => prev.filter((f) => address(f) !== address(field)));
  const closure = closureState(detail.overrides, hoursFields);

  return (
    <>
      <ContextBar
        environmentId={environmentId}
        environmentName={envName}
        live={live}
        onEnvironment={onEnvironment}
        actions={
          <>
            {hoursFields.length > 0 && (
              <button
                type="button"
                className={`${btnSecondary} ${closure.kind === "active" ? "!border-red-600 !bg-red-600 !text-white hover:!bg-red-700" : closure.kind === "scheduled" ? "!border-amber-500 !bg-amber-100 !text-amber-950 hover:!bg-amber-200 dark:!bg-amber-900 dark:!text-amber-50" : "!border-green-600 !text-green-800 dark:!text-green-300"}`}
                onClick={() => setClosing(true)}
                title={TOOLTIPS[closure.kind === "active" ? "flow.emergency-close.active" : closure.kind === "scheduled" ? "flow.emergency-close.scheduled" : "flow.emergency-close"]}
                aria-label={closure.kind === "active" ? "Emergency close is on - reopen" : closure.kind === "scheduled" ? "Emergency close is scheduled" : "Emergency close"}
              >
                {closure.kind === "active" ? "● Closed - emergency" : closure.kind === "scheduled" ? "◐ Close scheduled" : "Emergency close"}
              </button>
            )}
            <div className="relative" ref={moreRef}>
              <button type="button" className={`${btnSecondary} px-3`} aria-haspopup="menu" aria-expanded={more} aria-label="More actions" title="More actions" onClick={() => setMore((m) => !m)}>
                <span aria-hidden="true" className="text-lg leading-none">⋮</span>
              </button>
              {more && (
                <div role="menu" className="absolute right-0 z-20 mt-1 w-56 rounded-lg border border-neutral-200 bg-white py-1 text-sm shadow-lg dark:border-neutral-700 dark:bg-neutral-900">
                  <button role="menuitem" type="button" className="block w-full px-3 py-2 text-left hover:bg-neutral-100 dark:hover:bg-neutral-800" onClick={() => { exportProfile({ name: detail.profile.name, ...(detail.profile.description ? { description: detail.profile.description } : {}), fields: draft }); setMore(false); }}>
                    Export as JSON
                  </button>
                  <Link role="menuitem" to={`/flow-configuration/${environmentId}/profiles`} className="block px-3 py-2 hover:bg-neutral-100 dark:hover:bg-neutral-800" onClick={() => setMore(false)}>
                    Import a profile…
                  </Link>
                  {detail.chain.indexOf(environmentId) > 0 && (
                    <Link role="menuitem" to={`${base}/promote`} className="block px-3 py-2 hover:bg-neutral-100 dark:hover:bg-neutral-800" onClick={() => setMore(false)}>
                      Promote into {envName}…
                    </Link>
                  )}
                  <Link role="menuitem" to={`${base}/versions`} className="block px-3 py-2 hover:bg-neutral-100 dark:hover:bg-neutral-800" onClick={() => setMore(false)}>
                    Compare versions
                  </Link>
                </div>
              )}
            </div>
          </>
        }
      >
        <label className="flex items-center gap-2 text-sm">
          <span className="text-neutral-500">Profile</span>
          <select aria-label="Profile" value={profileId} onChange={(e) => onProfile(e.target.value)} className="min-h-9 min-w-52 rounded-lg border border-neutral-300 bg-white px-3 text-sm font-semibold text-ink dark:border-neutral-700 dark:bg-neutral-900 dark:text-white">
            {profiles.map((p) => (
              <option key={p.profileId} value={p.profileId}>
                {p.name}
              </option>
            ))}
            {!profiles.some((p) => p.profileId === profileId) && <option value={profileId}>{detail.profile.name}</option>}
          </select>
        </label>
        <SaveState detail={detail} envName={envName} dirty={dirty} saving={save.isPending} failed={save.isError} lastSave={lastSave} editingRequest={editingRequest} onRetry={flush} />
        <span className="text-sm text-neutral-600 dark:text-neutral-400">{bound.length === 0 ? "no alias bound" : bound.join(", ")}</span>
      </ContextBar>

      <MutationError of={save} />
      {detail.pendingRequest && !editingRequest && (
        <p role="status" className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-100">
          {detail.pendingRequest.updatedBy ?? detail.pendingRequest.requestedBy} has changes waiting for approval here ({detail.pendingRequest.promotionId}) - you are editing what is live; their request is on the{" "}
          <Link to={`/flow-configuration/${environmentId}/promotions`} className="underline">Promotions</Link> page.
        </p>
      )}
      {closing && hoursFields.length > 0 && (
        <EmergencyClose environmentId={environmentId} envName={envName} profileId={profileId} profileName={detail.profile.name} hoursFields={hoursFields} announcements={announcementNames} state={closure} writesEnabled={writesEnabled} onClose={() => setClosing(false)} />
      )}

      <nav aria-label="Profile sections" className="border-b border-neutral-200 dark:border-neutral-800">
        <ul className="flex flex-wrap gap-1">
          {TABS.filter((t) => (t.id !== "promote" || detail.chain.indexOf(environmentId) > 0) && (!t.legacy || draft.some((f) => t.types!.includes(f.type)))).map((t) => {
            const count = t.id === "holidays" ? hoursFields.reduce((n, h) => n + (h.value.holidays?.length ?? 0), 0) : t.types ? draft.filter((f) => t.types!.includes(f.type)).length : 0;
            return (
              <li key={t.id}>
                <NavLink
                  to={`${base}/${t.id}`}
                  aria-current={active.id === t.id ? "page" : undefined}
                  className={`inline-block border-b-2 px-3 py-2.5 text-sm font-medium transition-colors ${active.id === t.id ? "-mb-px border-accent text-accent-strong dark:text-white" : "border-transparent text-neutral-600 hover:border-neutral-300 hover:text-ink dark:text-neutral-400 dark:hover:text-white"}`}
                >
                  {t.label}
                  {count > 0 && <span className="ml-1.5 rounded-full bg-neutral-100 px-1.5 text-xs text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">{count}</span>}
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>

      {active.id === "overview" && (
        <Overview environmentId={environmentId} envName={envName} profileId={profileId} detail={detail} bound={bound} inventory={inventory} />
      )}
      {active.id === "hours" && (
        <HoursTab environmentId={environmentId} profileId={profileId} fields={hoursFields} draft={draft} inventory={inventory} overrides={detail.overrides} announcements={announcementNames} writesEnabled={writesEnabled} onUpdate={update} onRemove={remove} onAdd={(field) => setDraft([...draft, field])} />
      )}
      {active.id === "holidays" && (
        <HolidaysTab
          fields={hoursFields}
          announcements={announcementNames}
          connectOverrides={Object.fromEntries(hoursFields.flatMap((f) => { const item = inventory?.items.find((i) => i.type === "hours_of_operation" && i.name === f.value.connect_hours); return item?.detail ? [[f.name, item.detail.overrides]] : []; }))}
          onUpdate={update}
        />
      )}
      {active.id === "announcements" && (
        <AnnouncementsTab
          environmentId={environmentId}
          profileId={profileId}
          fields={draft.filter((f): f is Extract<ProfileField, { type: "announcement" }> => f.type === "announcement")}
          draft={draft}
          overrides={detail.overrides}
          onUpdate={update}
          onRemove={remove}
          onAdd={(field) => setDraft([...draft, field])}
          onReplaceAll={(next) => setDraft([...draft.filter((f) => f.type !== "announcement"), ...next])}
        />
      )}
      {active.id === "kv" && (
        <KvTab
          fields={draft.filter((f): f is Extract<ProfileField, { type: "kv" }> => f.type === "kv")}
          draft={draft}
          inventory={inventory}
          onUpdate={update}
          onRemove={remove}
          onAdd={(field) => setDraft([...draft, field])}
          onReplaceAll={(next) => setDraft([...draft.filter((f) => f.type !== "kv"), ...next])}
        />
      )}
      {active.types && active.id !== "hours" && active.id !== "announcements" && active.id !== "kv" && (
        <FieldsTab
          key={active.id}
          types={active.types}
          label={active.label}
          legacy={Boolean(active.legacy)}
          environmentId={environmentId}
          envName={envName}
          profileId={profileId}
          draft={draft}
          detail={detail}
          inventory={inventory}
          onUpdate={update}
          onRemove={remove}
          onAdd={(field) => setDraft([...draft, field])}
        />
      )}
      {active.id === "versions" && <Versions environmentId={environmentId} profileId={profileId} />}
      {active.id === "promote" && (
        <>
          <InventoryStateNote state={inventory?.state} environmentName={envName} />
          <PromotionPanel environmentId={environmentId} profileId={profileId} chain={detail.chain} />
        </>
      )}
    </>
  );
}

/**
 * #264: the bar reports sync state, not a version number. Saved is not yet live:
 * the environment's stack pulls within five minutes, and the bar says which
 * side of that line the profile is on. A gated non-manager sees their open
 * request instead. The number lives in History.
 */
function SaveState({ detail, envName, dirty, saving, failed, lastSave, editingRequest, onRetry }: { detail: ProfileDetailResponse; envName: string; dirty: boolean; saving: boolean; failed: boolean; lastSave: { at: string; promotionId?: string } | undefined; editingRequest: boolean; onRetry: () => void }) {
  if (failed && dirty) {
    return (
      <span className="flex items-center gap-2">
        <StatusPill status="not saved" tone="critical" />
        <button type="button" className={`${btnSecondary} min-h-8 px-2.5 text-xs`} onClick={onRetry}>Retry</button>
      </span>
    );
  }
  if (saving || dirty) return <StatusPill status="saving" tone="neutral" />;
  if (editingRequest) {
    const request = lastSave?.promotionId ?? detail.pendingRequest?.promotionId;
    return (
      <span className="flex items-center gap-2 text-sm">
        <StatusPill status={request ? "sent for approval" : "saves go for approval"} tone="warning" />
        {request && <span className="text-neutral-600 dark:text-neutral-400">{request} · a manager decides</span>}
        <Hint id="flow.save-state" label="what saving does here" />
      </span>
    );
  }
  const behind = Boolean(detail.conditions.syncLag ?? detail.conditions.pendingSync);
  const pulledAt = detail.conditions.syncLag?.syncedAt;
  if (detail.configVersion === 0) return <StatusPill status="nothing here yet" tone="neutral" />;
  return (
    <span className="flex items-center gap-2 text-sm">
      {behind ? <StatusPill status={`saved, ${envName} has not pulled it yet`} tone="warning" /> : <StatusPill status={`live in ${envName}`} tone="ok" />}
      {pulledAt ? <span className="text-neutral-600 dark:text-neutral-400">last pulled <RelativeTime iso={pulledAt} /></span> : lastSave ? <span className="text-neutral-600 dark:text-neutral-400">saved <RelativeTime iso={lastSave.at} /></span> : null}
      <Hint id="flow.save-state" label="what saving does here" />
    </span>
  );
}

function Overview({
  environmentId,
  envName,
  profileId,
  detail,
  bound,
  inventory,
}: {
  environmentId: string;
  envName: string;
  profileId: string;
  detail: ProfileDetailResponse;
  bound: string[];
  inventory: InventoryResponse | undefined;
}) {
  const nameOf = useEnvironmentName();
  const hours = detail.fields.filter((f): f is Extract<ProfileField, { type: "hours" }> => f.type === "hours");
  const below = detail.chain.slice(0, Math.max(0, detail.chain.indexOf(environmentId)));
  const source = below.at(-1);
  const base = `/flow-configuration/${environmentId}/profiles/${profileId}`;
  return (
    <div className="grid gap-5">
      <Conditions conditions={detail.conditions} environmentId={environmentId} />
      <InventoryStateNote state={inventory?.state} environmentName={envName} />
      <div className="grid gap-5 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        <div className="grid gap-5">
          {hours.length > 0 ? (
            hours.map((h) => {
              const now = hoursNow(h.value);
              return (
                <Card key={h.name} title={`Opening hours · ${h.name}`}>
                  <WeekStrip schedule={h.value} />
                  <p className="mt-3 text-sm text-neutral-600 dark:text-neutral-400">
                    Right now: <strong className={now.open ? "text-emerald-700 dark:text-emerald-300" : "text-amber-700 dark:text-amber-300"}>{now.open ? "open" : "closed"}</strong>
                    {now.reason === "holiday" && ` (${now.holiday})`}
                    {now.announcement && (
                      <>
                        {" "}· a caller hears <q>{now.announcement}</q>
                      </>
                    )}
                    {" "}· {h.value.timezone}
                  </p>
                </Card>
              );
            })
          ) : (
            <Card title="Opening hours">
              <p className="text-sm text-neutral-600 dark:text-neutral-400">
                No opening hours yet. <Link to={`${base}/hours`} className="underline">Add them</Link> and the flow can ask whether you are open.
              </p>
            </Card>
          )}
          <Card title="What this profile holds">
            <ul className="grid gap-1 text-sm sm:grid-cols-2">
              {TABS.filter((t) => t.types && (!t.legacy || detail.fields.some((f) => t.types!.includes(f.type)))).map((t) => {
                const n = detail.fields.filter((f) => t.types!.includes(f.type)).length;
                return (
                  <li key={t.id}>
                    <Link to={`${base}/${t.id}`} className="underline">{t.label}</Link> · {n}
                  </li>
                );
              })}
            </ul>
          </Card>
        </div>
        <div className="grid gap-5 self-start">
          <Card title="Aliases">
            {bound.length === 0 ? (
              <p className="text-sm text-neutral-600 dark:text-neutral-400">Nothing reaches this profile in {envName} yet - bind a number, or add a name a flow will pass.</p>
            ) : (
              <ul className="text-sm">{bound.map((n) => <li key={n}>{n}</li>)}</ul>
            )}
            <BindHere environmentId={environmentId} profileId={profileId} inventory={inventory} bound={bound} />
            <Link to={`/flow-configuration/${environmentId}/aliases`} className="mt-2 inline-block text-sm underline">Manage aliases</Link>
          </Card>
          {source && (
            <Card title={`Promote from ${nameOf(source)}`}>
              <p className="text-sm text-neutral-600 dark:text-neutral-400">
                References resolve by <em>name</em>, not ARN: a queue called "Tier 1 Queue" there becomes {envName}'s "Tier 1 Queue". A name with no match here is listed before anything is applied.
              </p>
              <Link to={`${base}/promote`} className={`${btnSecondary} mt-3`}>Preview promotion</Link>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

/** The instance's numbers not yet reaching any profile, bound from the profile itself (the owner asked how, 2026-09-18); or a name (ADR-0020). */
function BindHere({ environmentId, profileId, inventory, bound }: { environmentId: string; profileId: string; inventory: InventoryResponse | undefined; bound: string[] }) {
  const bindings = useBindings(environmentId);
  const bind = useBind(environmentId);
  const [name, setName] = useState("");
  const [problem, setProblem] = useState<string>();
  const taken = new Set((bindings.data?.bindings ?? []).map((b) => b.alias));
  const free = (inventory?.items ?? []).filter((i) => i.type === "phone_number" && !taken.has(i.name) && !bound.includes(i.name)).map((i) => i.name);
  return (
    <div className="mt-2 grid gap-2 text-sm">
      {free.length === 0 ? (
        <p className="text-xs text-neutral-500">Every number in the instance is bound. Numbers come from Amazon Connect: claim one there and it appears within about a minute.</p>
      ) : (
        <label className="block">
          <span className="text-neutral-500">Bind a number</span>
          <select className={`${inputClass} mt-1`} value="" aria-label="Bind a number to this profile" disabled={bind.isPending} onChange={(e) => e.target.value && bind.mutate({ alias: e.target.value, profileId })}>
            <option value="">Choose a number…</option>
            {free.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
      )}
      <form
        className="flex flex-wrap items-end gap-2"
        aria-label="Add a named alias to this profile"
        onSubmit={(e) => {
          e.preventDefault();
          const alias = name.trim().toLowerCase();
          if (!BindingAliasSchema.safeParse(alias).success || alias.startsWith("+")) return setProblem("Lower-case letters, digits, dot, underscore or hyphen: webchat-support, sales.");
          if (taken.has(alias)) return setProblem(`${alias} is already bound.`);
          bind.mutate({ alias, profileId }, { onSuccess: () => { setName(""); setProblem(undefined); } });
        }}
      >
        <label className="block">
          <span className="text-neutral-500">Add a name</span>
          <input className={`${inputClass} mt-1 w-44`} value={name} placeholder="webchat-support" aria-label="Named alias for this profile" onChange={(e) => { setName(e.target.value); setProblem(undefined); }} />
        </label>
        <button type="submit" className={`${btnSecondary} min-h-8 px-3 text-xs`} disabled={bind.isPending || !name.trim()}>Add</button>
        {problem && <p role="alert" className="basis-full text-xs text-red-700 dark:text-red-300">{problem}</p>}
      </form>
      <MutationError of={bind} />
      <p className="text-xs text-neutral-500">A flow reaches a name by setting the contact attribute <code>starsach_alias</code> before the lander or a Starsach module.</p>
    </div>
  );
}

const DAY = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function WeekStrip({ schedule }: { schedule: Extract<ProfileField, { type: "hours" }>["value"] }) {
  return (
    <ul className="grid grid-cols-7 gap-1.5 text-center text-xs" aria-label="Opening hours by day">
      {[1, 2, 3, 4, 5, 6, 0].map((d) => {
        const spans = schedule.weekly.filter((w) => w.day === d);
        return (
          <li key={d} className={`rounded-md px-1 py-2 ${spans.length ? "bg-accent-soft text-ink dark:bg-neutral-800 dark:text-white" : "bg-neutral-100 text-neutral-500 dark:bg-neutral-800/60"}`}>
            <span className="block text-neutral-500 dark:text-neutral-400">{DAY[d]}</span>
            {spans.length ? spans.map((s) => <span key={s.opens} className="block tabular-nums">{s.opens}–{s.closes}</span>) : "Closed"}
          </li>
        );
      })}
    </ul>
  );
}

function FieldsTab({
  types,
  label,
  legacy,
  environmentId,
  envName,
  profileId,
  draft,
  detail,
  inventory,
  onUpdate,
  onRemove,
  onAdd,
}: {
  types: FieldType[];
  label: string;
  legacy: boolean;
  environmentId: string;
  envName: string;
  profileId: string;
  draft: ProfileField[];
  detail: ProfileDetailResponse;
  inventory: InventoryResponse | undefined;
  onUpdate: (field: ProfileField, next: ProfileField) => void;
  onRemove: (field: ProfileField) => void;
  onAdd: (field: ProfileField) => void;
}) {
  const fields = draft.filter((f) => types.includes(f.type));
  const isReferences = types === REFERENCE_TYPES;
  return (
    <div className="grid gap-5">
      {isReferences && <InventoryStateNote state={inventory?.state} environmentName={envName} />}
      <Card title={label}>
        {fields.length === 0 && (
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            {isReferences ? "No references yet. A reference points a name in the flow at a real queue, flow, prompt, hours of operation or bot in this instance." : `No ${label.toLowerCase()} yet.`}
          </p>
        )}
        <ul className="space-y-4">
          {fields.map((field) => {
            const key = `${field.type}/${field.name}`;
            const override = detail.overrides.find((o) => o.field === key);
            const meta = detail.fieldMeta[key];
            return (
              <li key={key}>
                <div className="flex flex-wrap items-baseline gap-2">
                  <label htmlFor={`field-${key}`} className="text-sm font-medium">{field.name}</label>
                  {types.length > 1 && <span className="text-xs text-neutral-500">{FIELD_TYPE_LABELS[field.type]}</span>}
                  {meta?.locally_modified && <StatusPill status="changed here" tone="warning" />}
                  {override && <StatusPill status="overridden" tone="critical" />}
                  <button type="button" className="ml-auto text-xs text-neutral-500 underline hover:text-ink dark:hover:text-white" onClick={() => onRemove(field)} aria-label={`Remove ${field.name}`}>
                    Remove
                  </button>
                </div>
                <FieldEditor field={field} id={`field-${key}`} inventory={inventory} onChange={(next) => onUpdate(field, next)} />
                {field.type === "hours" && <RightNow schedule={field.value} />}
                {override && <OverrideRow environmentId={environmentId} profileId={profileId} field={key} override={override} describe={describeOverride} />}
              </li>
            );
          })}
        </ul>
        {legacy ? (
          <p className="mt-3 text-xs text-neutral-500">No more of these are added: a flow sets its own voice, and reads queues, flows, prompts and bots from the Connect cache by name.</p>
        ) : (
          <AddField key={label} existing={draft} inventory={inventory} onAdd={onAdd} only={types} />
        )}
        {!isReferences && fields.length > 0 && (
          <div className="mt-4">
            <NewOverride environmentId={environmentId} profileId={profileId} fields={fields} />
          </div>
        )}
      </Card>
    </div>
  );
}

function RightNow({ schedule }: { schedule: Extract<ProfileField, { type: "hours" }>["value"] }) {
  const now = hoursNow(schedule);
  return (
    <p className="mt-1 text-xs text-neutral-600 dark:text-neutral-400">
      Right now this reads as <strong>{now.open ? "open" : "closed"}</strong>
      {now.reason === "holiday" ? ` (${now.holiday})` : ""} in {schedule.timezone}.
    </p>
  );
}

function FieldEditor({ field, id, inventory, onChange }: { field: ProfileField; id: string; inventory: InventoryResponse | undefined; onChange: (next: ProfileField) => void }) {
  const input = "mt-1 w-full rounded border border-neutral-300 bg-white px-2 py-1 text-sm dark:border-neutral-700 dark:bg-neutral-900";
  if ("ref" in field) {
    const items = (inventory?.items ?? []).filter((i) => i.type === field.type);
    return (
      <select id={id} className={input} value={field.ref} onChange={(e) => onChange({ ...field, ref: e.target.value })}>
        {/* Real names from this instance: the customer never sees or types an ARN. */}
        {items.map((i) => (
          <option key={i.name} value={i.name}>{i.name}</option>
        ))}
        {!items.some((i) => i.name === field.ref) && <option value={field.ref}>{field.ref} (not in this instance)</option>}
      </select>
    );
  }
  if (field.type === "menu") return <MenuEditor field={field} inventory={inventory} onChange={(value) => onChange({ ...field, value })} />;
  if (field.type === "hours") return <HoursEditor field={field} onChange={(value) => onChange({ ...field, value })} />;
  return <input id={id} className={input} value={field.value} onChange={(e) => onChange({ ...field, value: e.target.value } as ProfileField)} />;
}

/** One line per field that differs between what is published and the draft - the "draft changes since v7" card. */
export function diffFields(published: ProfileField[], draft: ProfileField[]): string[] {
  const key = (f: ProfileField) => `${f.type}/${f.name}`;
  const before = new Map(published.map((f) => [key(f), f]));
  const after = new Map(draft.map((f) => [key(f), f]));
  const lines: string[] = [];
  for (const [k, f] of after) {
    const was = before.get(k);
    if (!was) lines.push(`${FIELD_TYPE_LABELS[f.type]} ${f.name} added`);
    else if (JSON.stringify(was) !== JSON.stringify(f)) lines.push(`${FIELD_TYPE_LABELS[f.type]} ${f.name} changed`);
  }
  for (const [k, f] of before) if (!after.has(k)) lines.push(`${FIELD_TYPE_LABELS[f.type]} ${f.name} removed`);
  return lines;
}

